import { isEmpty, isNil, uniqBy } from 'lodash';
import { useMemo } from 'react';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { FieldRecord } from '@shared/scenes/entity/types';
import {
  getManagedComponents,
  isManagingInputComponent,
  isRelatedCardComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';

export type CardEntityError = {
  description: string | null;
  componentId?: string;
  /** The inner subform component ID from a server-side COMPONENT_DATA_REQUIRED error. */
  subformComponentId?: string;
};

interface FormErrorsProps {
  apiErrors?: CardEntityError[];
  fields?: FieldRecord;
  components: API.AnyComponent[];
  /**
   * The workflow's full component set (overrides applied, nothing filtered out), which unlike
   * `components` still holds the managed children a managing component owns — the start/end DATE
   * components of a DATE_RANGE. Used to resolve required-ness for managing components. Falls back
   * to `components` when not provided. Matches {@link WorkflowComponentsContext}, which is how
   * fields below the form read the same set.
   */
  workflowComponents?: API.AnyComponent[];
  includeOnSubmitErrors?: boolean;
  includeLocalErrors?: boolean;
}

interface FormErrors {
  errors: CardEntityError[];
  isValid: boolean;
}

export function useFormErrors({
  apiErrors,
  fields,
  components,
  workflowComponents,
  includeOnSubmitErrors = true,
  includeLocalErrors = true,
}: FormErrorsProps): FormErrors {
  const requiredErrors = useRequiredErrors({ fields, components });
  const managedComponentRequiredErrors = useManagedComponentRequiredErrors({
    fields,
    components,
    workflowComponents,
  });
  const maxExceededErrors = useMaxExceededErrors({ fields, components });
  const localValidationErrors = useLocalValidationErrors({ fields });
  // Suppress stale server errors when the user has explicitly cleared the field.
  // Only outer SubformField-slot errors (identified by a non-nil subformComponentId)
  // are suppressed this way — they correspond to a field the user can clear. Inner
  // audit-field errors (remapped via subformApiErrors, no subformComponentId) must
  // always be shown because AuditField.fromFieldJson(undefined) returns null and
  // those null values mean "empty", not "cleared by the user".
  const serverErrors = (apiErrors ?? [])
    .filter(error => {
      if (isNil(error.componentId)) {
        return true;
      }
      if (fields?.[error.componentId] !== null) {
        return true;
      }
      // field slot is null — only suppress outer subform errors, keep inner ones.
      return isNil(error.subformComponentId);
    })
    // Managed components (e.g. the start/end DATE components a DATE_RANGE manages) are not
    // rendered as their own fields, so a server error keyed to one would otherwise be treated
    // as a hidden field. Remap it onto the managing parent so the error surfaces on the visible
    // parent field instead. Managed children live in `workflowComponents`, not `components`.
    .map(error => {
      if (isNil(error.componentId)) {
        return error;
      }
      const parentComponentId = workflowComponents?.find(
        c => c.id === error.componentId,
      )?.managedByComponentId;
      return isNil(parentComponentId) ? error : { ...error, componentId: parentComponentId };
    });

  // Errors whose component isn't rendered in this form are "hidden field" errors. Collapse all
  // of them into a single banner (pluralized when more than one) instead of one banner each.
  const hiddenFieldErrors = serverErrors.filter(
    error => !isNil(error.componentId) && !components.some(c => c.id === error.componentId),
  );
  const visibleServerErrors = serverErrors.filter(
    error => isNil(error.componentId) || components.some(c => c.id === error.componentId),
  );
  // Count distinct hidden components, not raw errors — one field can produce several errors
  // (and a managing parent collects its children's), so the banner pluralizes by field count.
  const hiddenFieldCount = new Set(hiddenFieldErrors.map(error => error.componentId)).size;
  const hasHiddenFieldError = hiddenFieldCount > 0;

  // When a hidden field error exists, drop the redundant generic form-level error the API adds
  // alongside it so only the hidden-field banner shows.
  const shownServerErrors = hasHiddenFieldError
    ? visibleServerErrors.filter(
        e => e.description !== i18n('card.form.errors.generic') || !isNil(e.componentId),
      )
    : visibleServerErrors;

  const hiddenFieldBanner: CardEntityError[] = hasHiddenFieldError
    ? [
        {
          description: i18n('card.form.errors.hiddenField', { count: hiddenFieldCount }),
          componentId: undefined,
        },
      ]
    : [];

  const dedupedServerErrors = uniqBy(
    [...shownServerErrors, ...hiddenFieldBanner],
    e => `${e.description ?? ''}::${e.componentId ?? ''}`,
  );

  let errors = [
    ...(includeOnSubmitErrors ? requiredErrors : []),
    ...(includeOnSubmitErrors ? managedComponentRequiredErrors : []),
    ...(includeOnSubmitErrors ? maxExceededErrors : []),
    ...(includeLocalErrors ? localValidationErrors : []),
    ...dedupedServerErrors,
  ];

  const hasComponentLevelErrors = errors.some(error => !isNil(error.componentId));
  const hasFormLevelErrors = errors.some(error => isNil(error.componentId));

  if (hasComponentLevelErrors && !hasFormLevelErrors && includeOnSubmitErrors) {
    errors = [...errors, { description: i18n('card.form.errorBanner'), componentId: undefined }];
  }

  return {
    errors,
    isValid:
      isEmpty(requiredErrors) &&
      isEmpty(managedComponentRequiredErrors) &&
      isEmpty(maxExceededErrors) &&
      isEmpty(localValidationErrors),
  };
}

function useRequiredErrors({
  fields,
  components,
}: {
  fields?: FieldRecord;
  components: API.AnyComponent[];
}): CardEntityError[] {
  return useMemo(
    () =>
      components
        .filter(component => 'required' in component && component.required === true)
        .filter(component => isNil(fields?.[component.id]) || fields?.[component.id]?.isEmpty)
        .map(component => ({
          description: i18n('card.form.errors.required'),
          componentId: component.id,
        })),
    [components, fields],
  );
}

// Managing components (e.g. DATE_RANGE) have no `required` field of their own — required-ness
// lives on the managed children they own, which are excluded from the visible `components`
// list. Resolve the children from `workflowComponents` (overrides applied): a managing component
// is required when ANY child is required, and is satisfied only when EVERY child is filled. The
// error is reported on the managing component id so it surfaces on its visible field.
function useManagedComponentRequiredErrors({
  fields,
  components,
  workflowComponents,
}: {
  fields?: FieldRecord;
  components: API.AnyComponent[];
  workflowComponents?: API.AnyComponent[];
}): CardEntityError[] {
  return useMemo(
    () =>
      components
        .filter(isManagingInputComponent)
        .filter(component => {
          const managedComponents = getManagedComponents(
            component,
            workflowComponents ?? components,
          );
          const isRequired = managedComponents.some(
            child => 'required' in child && child.required === true,
          );
          return (
            isRequired &&
            managedComponents.some(child => {
              const field = fields?.[child.id];
              return isNil(field) || field.isEmpty;
            })
          );
        })
        .map(component => ({
          description: i18n('card.form.errors.required'),
          componentId: component.id,
        })),
    [components, fields, workflowComponents],
  );
}

// Related card components can cap how many cards may be selected (`limit`). This
// is enforced like a required check: the count of selected cards is compared
// against the component's limit, producing a component-level error that drives
// the form banner, marks the field, blocks submit, and clears live as the
// selection changes. Mirrors the server-side ARRAY_MAX_SIZE_EXCEEDED check.
function useMaxExceededErrors({
  fields,
  components,
}: {
  fields?: FieldRecord;
  components: API.AnyComponent[];
}): CardEntityError[] {
  return useMemo(
    () =>
      components
        .filter(isRelatedCardComponent)
        .filter(component => !isNil(component.limit))
        .filter(component => {
          const field = fields?.[component.id];
          const count = field instanceof RelatedEntityField ? field.items.length : 0;
          return count > (component.limit ?? 0);
        })
        .map(component => ({
          description: i18n('card.form.errors.maxExceeded', { count: component.limit ?? 0 }),
          componentId: component.id,
        })),
    [components, fields],
  );
}

function useLocalValidationErrors({ fields }: { fields?: FieldRecord }): CardEntityError[] {
  return useMemo(
    () =>
      Object.keys(fields ?? {})
        .filter(componentId => !isNil(fields?.[componentId]?.validate()))
        .map(componentId => ({
          description: fields?.[componentId]?.validate() ?? '',
          componentId,
        })),
    [fields],
  );
}
